import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import CheckboxTable from 'src/components/common/field/CheckboxTable';
import DropDown from 'src/components/common/field/DropDown';
import InputCustom from 'src/components/common/field/InputCustom';
import IconButtonCustom from 'src/components/quotations/IconButtonCustom';

import { useEffect, useState } from 'react';
import { roundUpFnc, formatNumber } from 'src/common/helper';
import { DataQuoteType, ItemType } from 'src/components/quotations/TypeCustom';
import { getAPI } from 'src/utils/api';
import { getToken } from 'src/utils/auth';

const fontText = {
  fontSize: '14px',
  lineHeight: '14.4px',
  fontWeight: '700',
};

export const calDiscount = (
  price: number | null,
  qty: number | null,
  apply_discount: boolean,
  roundup: boolean,
  discount: number | string | null | undefined = undefined
) => {
  let priceCal = price;

  if (price && apply_discount && discount) {
    const priceDiscount = (price / 100) * Number(discount);

    priceCal = price - priceDiscount;
  }

  return priceCal
    ? (roundup ? roundUpFnc(priceCal, 1000) : priceCal) * Number(qty)
    : priceCal;
};

type RecordProps = {
  items: ItemType[];
  loading?: boolean;
  dataQuote: DataQuoteType;
  discount?: number | null;
  discountid?: number | string | null;
  onClickAddRow?: () => void;
  onClickDelete?: (index: number) => void;
  onChangeItem: (
    index: number,
    fieldName: string,
    value: string | number | boolean | null
  ) => void;
  handleChooseTemplate: (template: any) => void;
  displayPriceType: number[];
  error?: any;
};

const Record: React.FC<RecordProps> = (props) => {
  const [surgeries, setSurgeries] = useState<any[]>([]);
  const [templates, setTempates] = useState<any[]>([]);
  const [showTemplate, setShowTemplate] = useState<boolean>(false);

  const handleChangeDropDown =
    (index: number, fieldName: string) => (value: string | number) => {
      props.onChangeItem(index, fieldName, value);
    };

  const handleChangeInput =
    (index: number, fieldName: string) => (value: string | number | null) => {
      props.onChangeItem(index, fieldName, value);
    };

  const handleChangeCheckBox =
    (index: number, fieldName: string) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;

      props.onChangeItem(index, fieldName, checked);
    };

  const handleDeleteRow = (index: number) => () => {
    props.onClickDelete?.(index);
  };

  const calPriceDiscount = (item: ItemType, price: any) => {
    const { qty, apply_discount, roundup } = item;

    return calDiscount(price, qty, apply_discount, roundup, props.discount);
  };

  // const calPriceMonitorDiscount = (item: ItemType) => {
  //   const { price_salon, qty, apply_discount, roundup } = item;

  //   return calDiscount(
  //     price_salon,
  //     qty,
  //     apply_discount,
  //     roundup,
  //     props.discount
  //   );
  // };

  const convertItemType = (data: any[], id: string, name: string) => {
    const converted = data
      ? data.map((item: any) => ({
          id: String(item[id]),
          name: item[name],
        }))
      : [];

    return converted;
  };

  const checkError = (index: number, fieldName: string) => {
    return (
      props.error &&
      props.error.type === 'items' &&
      props.error.index === index &&
      props.error.fieldName === fieldName
    );
  };

  const handleTemplateClick = () => {
    if (showTemplate) {
      setShowTemplate(!showTemplate);
      return;
    }

    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }

      getAPI(`/template/quote-all?token=${token}`)
        .then((res: any) => {
          if (res.code === 200 && res.data) {
            setTempates(res.data);
            setShowTemplate(!showTemplate);
          }
        })
        .finally(() => {});
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  const handleChooseTemplate = (template: any) => {
    setShowTemplate(false);
    props.handleChooseTemplate(template);
  };

  useEffect(() => {
    if (props.dataQuote) {
      setSurgeries(convertItemType(props.dataQuote.surgeries, 'id', 'surgery'));
    }
  }, [props.dataQuote]);

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: 'none',
          '& *': { color: 'primary.dark', fontSize: '12px' },
        }}
      >
        <Table
          stickyHeader
          sx={{
            width: '100%',
            '& .MuiTableCell-root.MuiTableCell-head': {
              borderBottom: '0px',
              fontWeight: 700,
              fontSize: '12px',
              height: '36px',
              p: '0px 4px',
            },
          }}
          aria-label='simple table'
        >
          <TableHead>
            <TableRow>
              <TableCell
                align={'left'}
                sx={{ width: '30%', bgcolor: 'primary.light' }}
              >
                施術名
              </TableCell>
              <TableCell
                align={'left'}
                sx={{ width: '10%', bgcolor: 'primary.light' }}
              >
                数量
              </TableCell>
              <TableCell
                align={'left'}
                sx={{ width: '20%', bgcolor: 'primary.light' }}
              >
                通常価格
              </TableCell>
              {props.displayPriceType.includes(2) && (
                <TableCell
                  align={'left'}
                  sx={{ width: '20%', bgcolor: 'primary.light' }}
                >
                  部分モニター価格
                </TableCell>
              )}
              {props.displayPriceType.includes(3) && (
                <TableCell
                  align={'left'}
                  sx={{ width: '20%', bgcolor: 'primary.light' }}
                >
                  顔出しモニター価格
                </TableCell>
              )}
              <TableCell
                align={'left'}
                sx={{ width: '8%', bgcolor: 'primary.light' }}
              >
                割引対象
              </TableCell>
              <TableCell
                align={'left'}
                sx={{ width: '8%', bgcolor: 'primary.light' }}
              >
                切り上げる
              </TableCell>
              <TableCell
                align={'left'}
                sx={{ width: '3%', bgcolor: 'primary.light' }}
              ></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {props.loading ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Stack spacing={1}>
                    <Skeleton variant='rounded' width={'100%'} height={40} />
                    <Skeleton variant='rounded' width={'100%'} height={40} />
                    <Skeleton variant='rounded' width={'100%'} height={40} />
                  </Stack>
                </TableCell>
              </TableRow>
            ) : (
              props.items.map((item: ItemType, index: number) => (
                <TableRow
                  key={index}
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    '& .MuiTableCell-root.MuiTableCell-body': {
                      p: '4px',
                      borderBottom: '0px',
                      fontSize: '12px',
                      fontWeight: 400,
                      lineHeight: '14.4px',
                    },
                    height: '48px',
                  }}
                >
                  <TableCell
                    component='td'
                    scope='row'
                    sx={{ verticalAlign: 'top' }}
                  >
                    <DropDown
                      idSelect='surgeryid'
                      selected={item.surgeryid}
                      items={surgeries}
                      onChange={handleChangeDropDown(index, 'surgeryid')}
                      sxInput={{
                        '.MuiSvgIcon-root': { fontSize: '24px' },
                        width: '283px',
                      }}
                      sxMenuProps={{
                        width: '283px',
                      }}
                    />
                  </TableCell>
                  <TableCell
                    component='td'
                    scope='row'
                    sx={{ verticalAlign: 'top' }}
                  >
                    <InputCustom
                      name='qty'
                      type='number'
                      value={item.qty}
                      onChange={handleChangeInput(index, 'qty')}
                      sx={{ width: '100%' }}
                      error={checkError(index, 'qty')}
                    />
                    {item.apply_discount && (
                      <Box sx={{ my: '5px', ml: '15px' }}>
                        <Typography component='label' {...fontText}>
                          {'割引後価格'}
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell
                    component='td'
                    scope='row'
                    sx={{ verticalAlign: 'top' }}
                  >
                    <InputCustom
                      name='price'
                      type='number'
                      value={item.price}
                      onChange={handleChangeInput(index, 'price')}
                      sx={{ width: '100%' }}
                      error={checkError(index, 'price')}
                    />
                    {item.apply_discount && (
                      <Box sx={{ my: '5px', ml: '15px' }}>
                        <Typography component='label' {...fontText}>
                          {formatNumber(
                            Number(calPriceDiscount(item, item.price))
                          )}
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                  {props.displayPriceType.includes(2) && (
                    <TableCell
                      component='td'
                      scope='row'
                      sx={{ verticalAlign: 'top' }}
                    >
                      <InputCustom
                        name='price_partial'
                        type='number'
                        value={item.price_partial}
                        onChange={handleChangeInput(index, 'price_partial')}
                        sx={{ width: '100%' }}
                        error={checkError(index, 'price_partial')}
                      />
                      {item.apply_discount && (
                        <Box sx={{ my: '5px', ml: '15px' }}>
                          <Typography component='label' {...fontText}>
                            {formatNumber(
                              Number(calPriceDiscount(item, item.price_partial))
                            )}
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                  )}

                  {props.displayPriceType.includes(3) && (
                    <TableCell
                      component='td'
                      scope='row'
                      sx={{ verticalAlign: 'top' }}
                    >
                      <InputCustom
                        name='price_full_face'
                        type='number'
                        value={item.price_full_face}
                        onChange={handleChangeInput(index, 'price_full_face')}
                        sx={{ width: '100%' }}
                        error={checkError(index, 'price_full_face')}
                      />
                      {item.apply_discount && (
                        <Box sx={{ my: '5px', ml: '15px' }}>
                          <Typography component='label' {...fontText}>
                            {formatNumber(
                              Number(
                                calPriceDiscount(item, item.price_full_face)
                              )
                            )}
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                  )}
                  {/* <TableCell component='td' scope='row' sx={{ verticalAlign: 'top' }}>
                    <InputCustom
                      name='price_salon'
                      type='number'
                      value={item.price_salon}
                      onChange={handleChangeInput(index, 'price_salon')}
                      sx={{ width: '100%' }}
                      error={checkError(index, 'price_salon')}
                    />
                    {item.apply_discount && (
                      <Box sx={{ my: '5px', ml: '15px' }}>
                        <Typography component='label' {...fontText}>
                          {formatNumber(Number(calPriceMonitorDiscount(item)))}
                        </Typography>
                      </Box>
                    )}
                  </TableCell> */}
                  <TableCell
                    component='td'
                    scope='row'
                    sx={{
                      width: '24px',
                      height: '24px',
                      color: '#23B7E5 !important',
                      '& .MuiSvgIcon-root': {
                        fontSize: '24px',
                        color: '#23B7E5 !important',
                      },
                      verticalAlign: 'top',
                    }}
                  >
                    <Box
                      sx={{
                        height: '48px',
                        display: 'flex',
                        alignContent: 'center',
                      }}
                    >
                      <CheckboxTable
                        value={''}
                        label={''}
                        checked={item.apply_discount}
                        onChange={handleChangeCheckBox(index, 'apply_discount')}
                        sx={{
                          '& .MuiCheckbox-root .MuiBox-root': {
                            width: '24px',
                            height: '24px',
                          },
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell
                    component='td'
                    scope='row'
                    sx={{
                      width: '24px',
                      height: '24px',
                      color: '#23B7E5 !important',
                      '& .MuiSvgIcon-root': {
                        fontSize: '24px',
                        color: '#23B7E5 !important',
                      },
                      verticalAlign: 'top',
                    }}
                  >
                    <Box
                      sx={{
                        height: '48px',
                        display: 'flex',
                        alignContent: 'center',
                      }}
                    >
                      {item.apply_discount ? (
                        <CheckboxTable
                          value={''}
                          label={''}
                          checked={item.roundup}
                          onChange={handleChangeCheckBox(index, 'roundup')}
                          sx={{
                            '& .MuiCheckbox-root .MuiBox-root': {
                              width: '24px',
                              height: '24px',
                            },
                          }}
                        />
                      ) : (
                        <></>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    component='td'
                    scope='row'
                    sx={{ verticalAlign: 'top' }}
                  >
                    <Box
                      sx={{
                        height: '48px',
                        display: 'flex',
                        alignContent: 'center',
                      }}
                    >
                      {props.items.length > 1 && (
                        <IconButton
                          title='Delete row'
                          sx={{ p: 0 }}
                          onClick={handleDeleteRow(index)}
                        >
                          <img
                            src='/images/icons/ic-delete-bin-red.svg'
                            style={{ width: '20px', height: '20px' }}
                            alt='icon'
                          />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack direction={'row'}>
        <IconButtonCustom
          text='追加する'
          src='/images/ic-plus-blue.svg'
          tooltip='Add row'
          onClick={props.onClickAddRow}
        />

        <Box position={'relative'} sx={{ ml: '20px' }}>
          {/* <Chip
            label='テンプレートから追加'
            onClick={handleTemplateClick}
            sx={{ ml: '4px' }}
          /> */}
          <IconButtonCustom
            text='テンプレートから追加する'
            src='/images/icons/ic-paper-blue.svg'
            tooltip='Add template'
            onClick={handleTemplateClick}
          />
          <Box
            position={'absolute'}
            left={'180px'}
            top={0}
            zIndex={9999}
            display={showTemplate ? 'block' : 'none'}
          >
            {Array.isArray(templates) &&
              templates.map((template, index) => {
                return (
                  <Accordion sx={{ minWidth: '300px' }} key={index}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls='panel1-content'
                      id='panel1-header'
                      sx={{
                        '&.MuiButtonBase-root.MuiAccordionSummary-root.Mui-expanded':
                          { minHeight: '48px' },
                        '& .MuiAccordionSummary-content.Mui-expanded': {
                          margin: 0,
                        },
                      }}
                    >
                      {template.category}
                    </AccordionSummary>
                    <AccordionDetails>
                      {Array.isArray(template.tpls) && (
                        <List
                          sx={{
                            width: '100%',
                            maxWidth: 360,
                            maxHeight: 300,
                            bgcolor: 'background.paper',
                            overflow: 'auto',
                            position: 'relative',
                          }}
                          component='nav'
                          aria-labelledby='nested-list-subheader'
                        >
                          {template.tpls.map((temp: any, idx: number) => {
                            return (
                              <ListItemButton
                                key={idx}
                                onClick={() => handleChooseTemplate(temp)}
                              >
                                <ListItemText primary={temp.title} />
                              </ListItemButton>
                            );
                          })}
                        </List>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
          </Box>
        </Box>
      </Stack>
    </>
  );
};

export default Record;
